import React, { useState } from "react";
import { useToast } from "../components/ToastWrapper";
import Auth from "../Auth";
import { SudoButton } from "../sudokit/components/SudoButton";
import { Text } from "../sudokit/components/Text";
import VStack from "../sudokit/Layout/VStack";
import HStack from "../sudokit/Layout/HStack";
import validator from "validator";

const isEmail = (value) => {
  if (!value || typeof value !== 'string') return false;
  return validator.isEmail(value.trim());
};

const isDiscordId = (value) => {
  if (!value || typeof value !== 'string') return false;
  const discordIdRegex = /^\d{17,19}$/; // Discord IDs are typically 17-19 digits
  return discordIdRegex.test(value.trim());
};

const isUserId = (value) => {
  if (!value || typeof value !== 'string') return false;
  const userIdRegex = /^[a-zA-Z0-9_-]{20,}$/;
  return userIdRegex.test(value.trim());
};

const detectColumnTypes = (records) => {
  if (!records || records.length === 0) return {};

  const columnTypes = {};
  const headers = Object.keys(records[0]);

  headers.forEach(header => {
    let emailCount = 0;
    let discordIdCount = 0;
    let userIdCount = 0;

    const sampleSize = Math.min(10, records.length);

    for (let i = 0; i < sampleSize; i++) {
      const value = records[i][header];
      if (isDiscordId(value)) discordIdCount++;
      else if (isEmail(value)) emailCount++;
      else if (isUserId(value)) userIdCount++;
    }

    if (discordIdCount > sampleSize / 2) {
      columnTypes.discordId = header;
    } else if (emailCount > sampleSize / 2) {
      columnTypes.email = header;
    } else if (userIdCount > sampleSize / 2) {
      columnTypes.userId = header;
    }
  });

  return columnTypes;
};

const parseCSV = (csvText) => {
  const lines = csvText.trim().split('\n');
  if (lines.length < 2) {
    throw new Error('CSV must have at least a header row and one data row');
  }

  const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

  const records = [];
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;

    const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
    const record = {};

    headers.forEach((header, index) => {
      record[header] = values[index] || '';
    });

    records.push(record);
  }

  // Detect column types
  const columnTypes = detectColumnTypes(records);

  return { records, columnTypes };
};

export default function DiscordUpload() {
  const [file, setFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [columnTypes, setColumnTypes] = useState({});
  const [processing, setProcessing] = useState(false);
  const [results, setResults] = useState(null);
  const [validationErrors, setValidationErrors] = useState([]);
  const { setToast } = useToast();

  const handleFileSelect = async (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== "text/csv") {
        setToast("Please select a CSV file");
        return;
      }

      try {
        setFile(selectedFile);
        setResults(null);
        setValidationErrors([]);

        const csvText = await selectedFile.text();
        const { records, columnTypes: detectedColumns } = parseCSV(csvText);

        const errors = validateCSVData(records, detectedColumns);
        if (errors.length > 0) {
          setValidationErrors(errors);
          setCsvData(null);
          setColumnTypes({});
        } else {
          setCsvData(records);
          setColumnTypes(detectedColumns);
        }

      } catch (error) {
        setToast(`Failed to parse CSV: ${error.message}`);
        setCsvData(null);
        setValidationErrors([]);
      }
    }
  };

  const validateCSVData = (data, detectedColumns) => {
    const errors = [];

    if (!data || data.length === 0) {
      errors.push("CSV file is empty");
      return errors;
    }

    if (!detectedColumns.discordId) {
      errors.push("No Discord ID column detected. Discord IDs should be 17-19 digit numbers.");
    }

    if (!detectedColumns.email && !detectedColumns.userId) {
      errors.push("No email or user ID column detected. Need either email addresses or user IDs.");
    }

    // Validate each record using detected column mappings
    data.forEach((record, index) => {
      const rowNum = index + 2; // +2 because index is 0-based and we have a header row

      // Check Discord ID
      if (detectedColumns.discordId) {
        const discordId = record[detectedColumns.discordId];
        if (!discordId || !discordId.trim()) {
          errors.push(`Row ${rowNum}: Missing Discord ID`);
        } else if (!isDiscordId(discordId)) {
          errors.push(`Row ${rowNum}: Invalid Discord ID format (${discordId})`);
        }
      }

      // Check that we have either email or userId
      const email = detectedColumns.email ? record[detectedColumns.email] : '';
      const userId = detectedColumns.userId ? record[detectedColumns.userId] : '';

      if (!email?.trim() && !userId?.trim()) {
        errors.push(`Row ${rowNum}: Missing both email and user ID`);
      }

      // Validate email format if present
      if (email && !isEmail(email)) {
        errors.push(`Row ${rowNum}: Invalid email format (${email})`);
      }

      // Validate user ID format if present
      if (userId && !isUserId(userId)) {
        errors.push(`Row ${rowNum}: Invalid user ID format (${userId})`);
      }
    });

    return errors.slice(0, 20); // Limit to first 20 errors for display
  };

  const handleUpload = async () => {
    if (!csvData) {
      setToast("Please select and validate a CSV file first");
      return;
    }

    if (validationErrors.length > 0) {
      setToast("Please fix validation errors before uploading");
      return;
    }

    setProcessing(true);
    try {
      // Transform records to use standard field names
      const transformedRecords = csvData.map(record => ({
        email: columnTypes.email ? record[columnTypes.email] : undefined,
        userId: columnTypes.userId ? record[columnTypes.userId] : undefined,
        discordId: columnTypes.discordId ? record[columnTypes.discordId] : '',
      }));

      const result = await Auth.fetchWithIdToken("/api/admin/process-discord-data", {
        records: transformedRecords
      });

      if (result.success) {
        setResults(result);
        setToast(`Successfully processed ${result.updatedCount} users`);

        setFile(null);
        setCsvData(null);
        setColumnTypes({});
        setValidationErrors([]);
        const fileInput = document.getElementById("csv-file-input");
        if (fileInput) fileInput.value = "";
      } else {
        setToast(result.error || "Processing failed");
      }
    } catch (error) {
      setToast("Processing failed. Please try again.");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="p-6 bg-white rounded-lg shadow-sm border border-gray-200">
      <VStack spacing={6}>
        <div>
          <Text variant="heading2" className="mb-2">
            Discord ID CSV Upload
          </Text>
          <Text variant="body" className="text-gray-600">
            Upload a CSV file to associate email addresses or user IDs with Discord IDs.
          </Text>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <Text variant="body" className="font-medium mb-2">
            CSV Format - Smart Column Detection:
          </Text>
          <ul className="text-sm text-gray-700 space-y-1">
            <li>• <strong>Auto-Detection:</strong> Columns are detected by data patterns</li>
            <li>• <strong>Required Data:</strong> Discord IDs (17-19 digits) + Emails or User IDs</li>
            <li>• <strong>Comma bug:</strong> Other columns should not contain commas</li>
          </ul>
        </div>

        <VStack spacing={4}>
          <div>
            <label htmlFor="csv-file-input" className="block text-sm font-medium text-gray-700 mb-2">
              Select CSV File
            </label>
            <input
              id="csv-file-input"
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
              disabled={processing}
            />
          </div>

          {validationErrors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <Text variant="body" className="font-medium text-red-800 mb-2">
                Validation Errors:
              </Text>
              <ul className="text-sm text-red-700 space-y-1">
                {validationErrors.map((error, index) => (
                  <li key={index}>• {error}</li>
                ))}
              </ul>
            </div>
          )}

          {csvData && validationErrors.length === 0 && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <Text variant="body" className="font-medium text-green-800 mb-3">
                CSV Preview ({csvData.length} total records):
              </Text>

              {/* Column Detection Summary */}
              <div className="text-sm text-green-700 mb-4">
                <strong>Detected Columns:</strong>
                <div className="flex flex-wrap gap-4 mt-1">
                  {columnTypes.discordId && (
                    <span className="bg-green-100 px-2 py-1 rounded text-green-800">
                      Discord ID: "{columnTypes.discordId}"
                    </span>
                  )}
                  {columnTypes.email && (
                    <span className="bg-blue-100 px-2 py-1 rounded text-blue-800">
                      Email: "{columnTypes.email}"
                    </span>
                  )}
                  {columnTypes.userId && (
                    <span className="bg-purple-100 px-2 py-1 rounded text-purple-800">
                      User ID: "{columnTypes.userId}"
                    </span>
                  )}
                </div>
              </div>

              {/* Table Preview */}
              <div className="overflow-x-auto">
                <table className="min-w-full bg-white border border-gray-200 rounded-lg text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Row
                      </th>
                      {columnTypes.email && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-blue-600 uppercase tracking-wider border-b">
                          Email ({columnTypes.email})
                        </th>
                      )}
                      {columnTypes.userId && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-purple-600 uppercase tracking-wider border-b">
                          User ID ({columnTypes.userId})
                        </th>
                      )}
                      {columnTypes.discordId && (
                        <th className="px-3 py-2 text-left text-xs font-medium text-green-600 uppercase tracking-wider border-b">
                          Discord ID ({columnTypes.discordId})
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {csvData.slice(0, 10).map((record, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-3 py-2 text-gray-500 font-medium">
                          {index + 1}
                        </td>
                        {columnTypes.email && (
                          <td className="px-3 py-2 text-gray-900">
                            {record[columnTypes.email] || '-'}
                          </td>
                        )}
                        {columnTypes.userId && (
                          <td className="px-3 py-2 text-gray-900">
                            {record[columnTypes.userId] || '-'}
                          </td>
                        )}
                        {columnTypes.discordId && (
                          <td className="px-3 py-2 text-gray-900 font-mono">
                            {record[columnTypes.discordId] || '-'}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {csvData.length > 10 && (
                <div className="text-xs text-green-600 mt-2">
                  Showing first 10 of {csvData.length} records
                </div>
              )}
            </div>
          )}

          <HStack spacing={3}>
            <SudoButton
              variant="primary"
              onClick={handleUpload}
              disabled={!csvData || validationErrors.length > 0 || processing}
              loading={processing}
            >
              {processing ? "Processing..." : "Process Discord Updates"}
            </SudoButton>

            {file && (
              <SudoButton
                variant="secondary"
                onClick={() => {
                  setFile(null);
                  setCsvData(null);
                  setColumnTypes({});
                  setValidationErrors([]);
                  setResults(null);
                  const fileInput = document.getElementById("csv-file-input");
                  if (fileInput) fileInput.value = "";
                }}
                disabled={processing}
              >
                Clear
              </SudoButton>
            )}
          </HStack>
        </VStack>

        {results && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <Text variant="body" className="font-medium text-green-800 mb-2">
              Upload Results:
            </Text>
            <div className="text-sm text-green-700 space-y-1">
              <div>• Total rows processed: {results.totalRows}</div>
              <div>• Successfully updated: {results.updatedCount}</div>
              <div>• Skipped (user not found): {results.skippedCount}</div>
              {results.errors && results.errors.length > 0 && (
                <div>• Errors: {results.errors.length}</div>
              )}
            </div>

            {results.errors && results.errors.length > 0 && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded">
                <Text variant="body" className="font-medium text-red-800 mb-1">
                  Errors:
                </Text>
                <ul className="text-sm text-red-700 space-y-1">
                  {results.errors.slice(0, 10).map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                  {results.errors.length > 10 && (
                    <li className="text-red-600">... and {results.errors.length - 10} more errors</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </VStack>
    </div>
  );
}
